/**
 * Web post-pair collision disambiguation prompt (Phase 7 — mirrors
 * the Android `CollisionDisambiguationStep.kt` and the extension's
 * `apps/extension/src/shared/ui/PairConfirmCardSlot/CollisionDisambiguation.tsx`).
 * Rendered on MainPage when `pairAutoNameResult.collision === true`.
 *
 * Three context-prefixed chip suggestions, a free-text custom name
 * (which overrides the chip selection), a primary Save, and a
 * tertiary "Use the original name anyway" link. Stateless — the
 * parent slot owns the rename API call and storage clear.
 */

import { MAX_DEVICE_NAME_LENGTH } from "@reflecto/client-core";
import { Button } from "@reflecto/client-ui";
import { type CSSProperties, useState } from "react";
import { messages } from "../i18n";

/**
 * Suggestion chip — module-scope so the function reference is stable across renders.
 * Defining it inside `CollisionDisambiguation` would create a new component identity on
 * every keystroke, causing React to unmount/remount each chip and lose keyboard focus.
 */
function Chip({
  name,
  selected,
  saving,
  onSelect,
}: {
  name: string;
  selected: boolean;
  saving: boolean;
  onSelect: (name: string) => void;
}) {
  return (
    <button
      type="button"
      style={selected ? chipSelectedStyle : chipStyle}
      aria-pressed={selected}
      onClick={() => onSelect(name)}
      disabled={saving}
    >
      {name}
    </button>
  );
}

export interface CollisionDisambiguationProps {
  originalName: string;
  onSave: (name: string) => Promise<void> | void;
  onUseAnyway: () => Promise<void> | void;
}

export function CollisionDisambiguation({
  originalName,
  onSave,
  onUseAnyway,
}: CollisionDisambiguationProps) {
  const work = messages.collision.chipWork(originalName);
  const home = messages.collision.chipHome(originalName);
  const personal = messages.collision.chipPersonal(originalName);
  const [selected, setSelected] = useState<string | null>(null);
  const [custom, setCustom] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  // Resolve the user's chosen name (custom text > chip > nothing). When neither is set,
  // `finalName` is null and Save is disabled — falling back to `originalName` would let
  // the user "rename" to the colliding name they already have, which is silently the same
  // outcome as "Use the original name anyway".
  const finalName = (() => {
    const trimmed = custom.trim();
    if (trimmed) return trimmed;
    return selected;
  })();
  const canSave = finalName != null && !saving;

  const handleSave = async () => {
    if (finalName == null) return; // belt-and-suspenders; Save is disabled in this state.
    setSaving(true);
    setError(false);
    try {
      await onSave(finalName);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleChipSelect = (name: string) => {
    setSelected(name);
    setCustom("");
  };

  return (
    <section
      aria-label={messages.collision.ariaLabel}
      data-testid="collision-disambiguation"
      style={rootStyle}
    >
      <h2 style={titleStyle}>{messages.collision.title(originalName)}</h2>
      <p style={subtitleStyle}>{messages.collision.subtitle}</p>
      <div style={chipsStyle}>
        <Chip
          name={work}
          selected={selected === work}
          saving={saving}
          onSelect={handleChipSelect}
        />
        <Chip
          name={home}
          selected={selected === home}
          saving={saving}
          onSelect={handleChipSelect}
        />
        <Chip
          name={personal}
          selected={selected === personal}
          saving={saving}
          onSelect={handleChipSelect}
        />
      </div>
      <label style={customLabelStyle}>
        <span>{messages.collision.customLabel}</span>
        <input
          type="text"
          style={customInputStyle}
          value={custom}
          maxLength={MAX_DEVICE_NAME_LENGTH}
          onChange={(e) => {
            setCustom(e.target.value);
            setSelected(null);
          }}
          disabled={saving}
        />
      </label>
      <Button
        variant="primary"
        size="m"
        fullWidth
        loading={saving}
        onClick={handleSave}
        disabled={!canSave}
      >
        {messages.collision.save}
      </Button>
      {error && (
        <span style={errorStyle} role="alert">
          {messages.collision.saveFailed}
        </span>
      )}
      <button type="button" style={useAnywayLinkStyle} onClick={onUseAnyway} disabled={saving}>
        {messages.collision.useAnyway}
      </button>
    </section>
  );
}

const rootStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  background: "var(--reflecto-surface-container, #ffffff)",
  color: "var(--reflecto-text-primary, #171717)",
  margin: "12px 16px",
  borderRadius: 12,
  border: "1px solid var(--reflecto-border-subtle, rgba(0,0,0,0.05))",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 18,
  lineHeight: "24px",
  fontWeight: 600,
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--reflecto-text-secondary, #404040)",
};

const chipsStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const chipStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  padding: "6px 12px",
  borderRadius: 16,
  border: "1px solid var(--reflecto-border, #d4d4d4)",
  background: "var(--reflecto-surface, #ffffff)",
  color: "var(--reflecto-text-primary, #171717)",
  cursor: "pointer",
};

const chipSelectedStyle: CSSProperties = {
  ...chipStyle,
  background: "var(--reflecto-accent, #10b981)",
  borderColor: "var(--reflecto-accent, #10b981)",
  color: "var(--reflecto-on-accent, #ffffff)",
};

const customLabelStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 13,
  color: "var(--reflecto-text-secondary, #404040)",
};

const customInputStyle: CSSProperties = {
  font: "inherit",
  fontSize: 14,
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--reflecto-border, #d4d4d4)",
};

const useAnywayLinkStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  background: "none",
  border: "none",
  color: "var(--reflecto-link, #0d9488)",
  cursor: "pointer",
  textAlign: "center",
  padding: 4,
};

const errorStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--reflecto-error, #b00020)",
  textAlign: "center",
};
